// My Team — customer's roster of agents (or anyone they make designs for).
// Each agent stores name, contact info, headshot, license #, etc. — and
// (Ship 3) can be assigned to designs so the admin can quickly find/reorder
// "all of John Smith's signs" without naming each design "John Smith - X".
//
// Solo customers get an auto-created is_self entry on first load so the
// same picker UX works for them without manual setup.

import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Pencil, Archive, ArchiveRestore, Image as ImageIcon, X, LayoutGrid } from 'lucide-react';
import {
  listAgents,
  createAgent,
  patchAgent,
  archiveAgent,
  restoreAgent,
  uploadAgentHeadshot,
  clearAgentHeadshot,
  listAgentDesigns,
  assignDesignsToAgent,
  unassignAgentFromDesign,
  listDesigns,
  reorderDesign,
  forkDesign,
  dispatchCartAdd,
  type Agent,
  StudioApiError,
} from '../../lib/api';
import { useStudio } from '../../lib/store';
import './my-team.css';

export function MyTeamView() {
  const qc = useQueryClient();
  const pushToast = useStudio((s) => s.pushToast);
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Agent | null>(null);
  const [creating, setCreating] = useState(false);
  // Agent whose assigned designs we're viewing in the modal.
  const [viewingDesigns, setViewingDesigns] = useState<Agent | null>(null);

  const query = useQuery({
    queryKey: ['agents', { archived: showArchived }],
    queryFn: () => listAgents({ archived: showArchived }),
  });

  const archiveMut = useMutation({
    mutationFn: (id: string) => archiveAgent(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['agents'] });
      pushToast({ type: 'success', message: 'Member archived' });
    },
    onError: (err) =>
      pushToast({
        type: 'error',
        message: err instanceof StudioApiError ? err.message : 'Could not archive',
      }),
  });

  const restoreMut = useMutation({
    mutationFn: (id: string) => restoreAgent(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['agents'] });
      pushToast({ type: 'success', message: 'Member restored' });
    },
  });

  const agents = query.data?.agents ?? [];
  const filtered = search.trim()
    ? agents.filter((a) => {
        const hay = `${a.first_name} ${a.last_name ?? ''} ${a.email ?? ''} ${a.title ?? ''} ${a.brokerage ?? ''}`.toLowerCase();
        return hay.includes(search.trim().toLowerCase());
      })
    : agents;

  return (
    <section aria-label="My Team">
      <header className="view-head">
        <h1 className="serif-italic">My Team</h1>
        <p className="sub">
          Add the agents you order signs for. Once linked, you can assign designs to specific
          team members — and (coming soon) auto-fill their info into templates.
        </p>
      </header>

      <div className="team-toolbar">
        <input
          type="search"
          className="team-search"
          placeholder="Search by name, email, title…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search team members"
        />
        <label className="team-toolbar__archived">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          <span>Show archived</span>
        </label>
        <button type="button" className="btn btn--hero" onClick={() => setCreating(true)}>
          <UserPlus size={16} aria-hidden="true" strokeWidth={1.6} />
          Add team member
        </button>
      </div>

      {query.isLoading ? (
        <div className="state">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="state">
          {search.trim() ? (
            <p>No matches for &ldquo;{search}&rdquo;.</p>
          ) : (
            <p>No team members yet. Click &ldquo;Add team member&rdquo; to get started.</p>
          )}
        </div>
      ) : (
        <div className="team-grid">
          {filtered.map((a) => (
            <AgentCard
              key={a.id}
              agent={a}
              onEdit={() => setEditing(a)}
              onViewDesigns={() => setViewingDesigns(a)}
              onArchive={() => archiveMut.mutate(a.id)}
              onRestore={() => restoreMut.mutate(a.id)}
            />
          ))}
        </div>
      )}

      {(editing || creating) && (
        <AgentEditor
          agent={editing}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
          onSaved={() => {
            void qc.invalidateQueries({ queryKey: ['agents'] });
          }}
        />
      )}

      {viewingDesigns && (
        <AgentDesignsModal
          agent={viewingDesigns}
          onClose={() => setViewingDesigns(null)}
        />
      )}
    </section>
  );
}

function AgentCard({
  agent,
  onEdit,
  onViewDesigns,
  onArchive,
  onRestore,
}: {
  agent: Agent;
  onEdit: () => void;
  onViewDesigns: () => void;
  onArchive: () => void;
  onRestore: () => void;
}) {
  const fullName = [agent.first_name, agent.last_name].filter(Boolean).join(' ');
  return (
    <article className={`agent-card${agent.archived_at ? ' is-archived' : ''}`}>
      <div className="agent-card__head">
        {agent.headshot_url ? (
          <img className="agent-card__headshot" src={agent.headshot_url} alt={fullName} />
        ) : (
          <div className="agent-card__headshot agent-card__headshot--placeholder" aria-hidden="true">
            {agent.first_name.charAt(0).toUpperCase()}
            {agent.last_name?.charAt(0).toUpperCase() ?? ''}
          </div>
        )}
        <div className="agent-card__name">
          <h3>{fullName}</h3>
          {agent.is_self && <span className="pill pill--gold">This is you</span>}
          {agent.title && <p className="agent-card__title">{agent.title}</p>}
        </div>
      </div>
      <dl className="agent-card__meta">
        {agent.phone && (
          <div>
            <dt>Phone</dt>
            <dd>{agent.phone}</dd>
          </div>
        )}
        {agent.email && (
          <div>
            <dt>Email</dt>
            <dd>{agent.email}</dd>
          </div>
        )}
        {agent.website && (
          <div>
            <dt>Website</dt>
            <dd>
              <a href={agent.website} target="_blank" rel="noreferrer">
                {agent.website.replace(/^https?:\/\//, '')}
              </a>
            </dd>
          </div>
        )}
      </dl>
      <div className="agent-card__actions">
        <button
          type="button"
          className="btn btn--primary btn--small"
          onClick={onViewDesigns}
          title="See and manage designs assigned to this member"
        >
          <LayoutGrid size={13} aria-hidden="true" strokeWidth={1.8} /> Designs
        </button>
        <button type="button" className="btn btn--secondary btn--small" onClick={onEdit}>
          <Pencil size={13} aria-hidden="true" strokeWidth={1.8} /> Edit
        </button>
        {agent.archived_at ? (
          <button type="button" className="btn btn--ghost btn--small" onClick={onRestore}>
            <ArchiveRestore size={13} aria-hidden="true" strokeWidth={1.8} /> Restore
          </button>
        ) : !agent.is_self ? (
          <button type="button" className="btn btn--ghost btn--small" onClick={onArchive}>
            <Archive size={13} aria-hidden="true" strokeWidth={1.8} /> Archive
          </button>
        ) : null}
      </div>
    </article>
  );
}

/** Modal showing all designs assigned to a single agent + a picker to add
 *  more. The picker pulls the customer's full library and lets them check
 *  designs to assign. */
function AgentDesignsModal({ agent, onClose }: { agent: Agent; onClose: () => void }) {
  const qc = useQueryClient();
  const pushToast = useStudio((s) => s.pushToast);
  const [adding, setAdding] = useState(false);

  const designsQuery = useQuery({
    queryKey: ['agent-designs', agent.id],
    queryFn: () => listAgentDesigns(agent.id),
  });

  const unassignMut = useMutation({
    mutationFn: (designId: string) => unassignAgentFromDesign(designId, agent.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['agent-designs', agent.id] });
      void qc.invalidateQueries({ queryKey: ['designs'] });
    },
  });

  const reorderMut = useMutation({
    mutationFn: async (designId: string) => {
      const desc = await reorderDesign(designId);
      return dispatchCartAdd(desc);
    },
    onSuccess: (cartUrl) => {
      window.location.href = cartUrl;
    },
    onError: (err) =>
      pushToast({
        type: 'error',
        message: err instanceof StudioApiError ? err.message : 'Could not reorder',
      }),
  });

  const forkMut = useMutation({
    // Pass agent_id so the cloned DH project gets {{agent_*}} placeholders
    // pre-filled with this team member's info (Ship 4 auto-fill). The
    // backend also auto-assigns the new fork to this agent so the chain
    // of designs stays attributed to them.
    mutationFn: (designId: string) => forkDesign(designId, { agent_id: agent.id }),
    onSuccess: (resp) => {
      window.location.href = resp.editor_url;
    },
    onError: () => pushToast({ type: 'error', message: 'Could not edit a copy' }),
  });

  // Use any for the design rows; backend returns a subset shape that's
  // not the full Design type. We only render thumbnail + name + chip.
  type AgentDesignRow = {
    id: string;
    custom_name: string | null;
    product_title: string | null;
    thumbnail_url: string | null;
    archived_at: string | null;
  };
  const designs = (designsQuery.data?.designs ?? []) as AgentDesignRow[];

  const fullName = [agent.first_name, agent.last_name].filter(Boolean).join(' ');

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal--md agent-designs-modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal__header">
          <div>
            <h3>{fullName}&rsquo;s designs</h3>
            <p className="modal__subtitle">
              {designs.length} design{designs.length === 1 ? '' : 's'} assigned
            </p>
          </div>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="modal__body">
          <div style={{ marginBottom: 16 }}>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => setAdding(true)}
            >
              + Add designs
            </button>
          </div>

          {designsQuery.isLoading ? (
            <div className="state">Loading…</div>
          ) : designs.length === 0 ? (
            <div className="state">
              <p>No designs assigned to {agent.first_name} yet.</p>
              <p style={{ fontSize: '0.84rem', color: 'var(--ink-soft)' }}>
                Click &ldquo;Add designs&rdquo; to attribute existing designs, or assign from
                the My Designs page.
              </p>
            </div>
          ) : (
            <ul className="agent-designs-list">
              {designs.map((d) => (
                <li key={d.id} className="agent-designs-list__item">
                  {d.thumbnail_url ? (
                    <img src={d.thumbnail_url} alt="" className="agent-designs-list__thumb" />
                  ) : (
                    <div className="agent-designs-list__thumb agent-designs-list__thumb--placeholder" />
                  )}
                  <div className="agent-designs-list__name">
                    {d.custom_name ?? d.product_title ?? 'Untitled'}
                  </div>
                  <div className="agent-designs-list__actions">
                    <button
                      type="button"
                      className="btn btn--primary btn--small"
                      onClick={() => reorderMut.mutate(d.id)}
                      disabled={reorderMut.isPending}
                      title={`Reorder this design as-is`}
                    >
                      Order
                    </button>
                    <button
                      type="button"
                      className="btn btn--secondary btn--small"
                      onClick={() => forkMut.mutate(d.id)}
                      disabled={forkMut.isPending}
                      title={`Open in editor and customize for ${agent.first_name}`}
                    >
                      Edit a copy
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--small"
                      onClick={() => unassignMut.mutate(d.id)}
                      disabled={unassignMut.isPending}
                      title={`Remove from ${agent.first_name}'s designs (the design itself stays in your library)`}
                    >
                      Unassign
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="modal__footer">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
        </footer>
      </div>

      {adding && (
        <AddDesignsModal
          agent={agent}
          alreadyAssigned={new Set(designs.map((d) => d.id))}
          onClose={() => setAdding(false)}
          onAdded={(count) => {
            setAdding(false);
            pushToast({
              type: 'success',
              message: `Assigned ${count} design${count === 1 ? '' : 's'} to ${agent.first_name}`,
            });
            void qc.invalidateQueries({ queryKey: ['agent-designs', agent.id] });
            void qc.invalidateQueries({ queryKey: ['designs'] });
          }}
        />
      )}
    </div>
  );
}

function AddDesignsModal({
  agent,
  alreadyAssigned,
  onClose,
  onAdded,
}: {
  agent: Agent;
  alreadyAssigned: Set<string>;
  onClose: () => void;
  onAdded: (count: number) => void;
}) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const designsQuery = useQuery({
    queryKey: ['designs-for-agent-add', search],
    queryFn: () =>
      listDesigns({ search: search || undefined, sort: 'recently_edited', limit: 60 }),
  });

  const assignMut = useMutation({
    mutationFn: () => assignDesignsToAgent(agent.id, Array.from(selected)),
    onSuccess: (resp) => onAdded(resp.assigned),
  });

  const designs = (designsQuery.data?.items ?? []).filter((d) => !alreadyAssigned.has(d.id));

  function toggle(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  return (
    <div className="modal-backdrop" onClick={onClose} style={{ zIndex: 60 }}>
      <div
        className="modal modal--md agent-designs-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal__header">
          <h3>Add designs to {agent.first_name}</h3>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="modal__body">

        <input
          type="search"
          className="agent-picker__search"
          placeholder="Search designs by name or product…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {designsQuery.isLoading ? (
          <div className="state">Loading…</div>
        ) : designs.length === 0 ? (
          <div className="state">No designs available.</div>
        ) : (
          <ul className="agent-picker__list">
            {designs.map((d) => (
              <li
                key={d.id}
                className={`agent-picker__row${selected.has(d.id) ? ' is-checked' : ''}`}
                onClick={() => toggle(d.id)}
              >
                <input
                  type="checkbox"
                  checked={selected.has(d.id)}
                  onChange={() => toggle(d.id)}
                  onClick={(e) => e.stopPropagation()}
                />
                {d.thumbnail.url ? (
                  <img src={d.thumbnail.url} alt="" className="agent-picker__avatar" />
                ) : (
                  <span className="agent-picker__avatar agent-picker__avatar--placeholder" />
                )}
                <div className="agent-picker__info">
                  <div className="agent-picker__name">
                    {d.name ?? d.shopify.product_title ?? 'Untitled design'}
                  </div>
                  {d.shopify.product_title && d.name && (
                    <div className="agent-picker__title">{d.shopify.product_title}</div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        </div>{/* /modal__body */}

        <footer className="modal__footer">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--hero"
            disabled={selected.size === 0 || assignMut.isPending}
            onClick={() => assignMut.mutate()}
          >
            {assignMut.isPending
              ? 'Adding…'
              : `Add ${selected.size} design${selected.size === 1 ? '' : 's'}`}
          </button>
        </footer>
      </div>
    </div>
  );
}

function AgentEditor({
  agent,
  onClose,
  onSaved,
}: {
  agent: Agent | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = !agent;
  const pushToast = useStudio((s) => s.pushToast);
  // Single combined name field — simpler UX, and the DH auto-fill uses
  // the full string as {{agent_name}} anyway. We split on the first space
  // when persisting so the DB columns stay populated for any code path
  // that needs first/last separately.
  const initialName =
    [agent?.first_name, agent?.last_name].filter(Boolean).join(' ') || '';
  const [name, setName] = useState(initialName);
  const [form, setForm] = useState({
    title: agent?.title ?? '',
    email: agent?.email ?? '',
    phone: agent?.phone ?? '',
    website: agent?.website ?? '',
    notes: agent?.notes ?? '',
  });
  const [headshotPreview, setHeadshotPreview] = useState<string | null>(agent?.headshot_url ?? null);
  const [headshotFile, setHeadshotFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const saveMut = useMutation({
    mutationFn: async () => {
      // Split combined name on first space — "John David Smith" →
      // first="John", last="David Smith". Single-word names go entirely
      // into first_name with last_name null.
      const trimmed = name.trim();
      const firstSpace = trimmed.indexOf(' ');
      const firstName = firstSpace === -1 ? trimmed : trimmed.slice(0, firstSpace);
      const lastName = firstSpace === -1 ? null : trimmed.slice(firstSpace + 1).trim();

      const body: Record<string, unknown> = { first_name: firstName };
      if (isNew) {
        if (lastName) body.last_name = lastName;
        if (form.title.trim()) body.title = form.title.trim();
        if (form.email.trim()) body.email = form.email.trim();
        if (form.phone.trim()) body.phone = form.phone.trim();
        if (form.website.trim()) body.website = form.website.trim();
        if (form.notes.trim()) body.notes = form.notes.trim();
      } else {
        // PATCH: send all known fields so explicit clears (empty → null) land.
        body.last_name = lastName;
        body.title = form.title.trim() || null;
        body.email = form.email.trim() || null;
        body.phone = form.phone.trim() || null;
        body.website = form.website.trim() || null;
        body.notes = form.notes.trim() || null;
      }
      const result = isNew
        ? await createAgent(body as Partial<Agent>)
        : await patchAgent(agent!.id, body as Partial<Agent>);

      if (headshotFile) {
        await uploadAgentHeadshot(result.agent.id, headshotFile);
      }
      return result.agent;
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (err) => {
      setError(err instanceof StudioApiError ? err.message : (err as Error).message);
    },
  });

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      setError('Please pick an image file.');
      return;
    }
    // 15 MB cap matches the server. Original-resolution preserved — no
    // resize, no compression on either side.
    if (f.size > 15 * 1024 * 1024) {
      setError('Image must be under 15 MB.');
      return;
    }
    setError(null);
    setHeadshotFile(f);
    const reader = new FileReader();
    reader.onload = () => setHeadshotPreview(reader.result as string);
    reader.readAsDataURL(f);
  }

  async function clearHeadshot() {
    setHeadshotFile(null);
    setHeadshotPreview(null);
    if (agent?.headshot_storage_path) {
      try {
        await clearAgentHeadshot(agent.id);
        pushToast({ type: 'success', message: 'Headshot removed' });
      } catch {
        pushToast({ type: 'error', message: 'Could not clear headshot' });
      }
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal--md agent-editor" onClick={(e) => e.stopPropagation()}>
        <header className="modal__header">
          <h3>{isNew ? 'Add team member' : `Edit ${agent?.first_name ?? 'member'}`}</h3>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="modal__body">

        {error && <div className="error-banner">{error}</div>}

        <div className="agent-editor__head">
          <div className="agent-editor__photo-col">
            <button
              type="button"
              className="agent-editor__headshot"
              onClick={() => fileRef.current?.click()}
              aria-label={headshotPreview ? 'Replace photo' : 'Add photo'}
            >
              {headshotPreview ? (
                <img src={headshotPreview} alt="Headshot preview" />
              ) : (
                <span className="agent-editor__headshot-placeholder" aria-hidden="true">
                  <ImageIcon size={28} strokeWidth={1.4} />
                  <span>Add photo</span>
                </span>
              )}
              <span className="agent-editor__headshot-overlay">
                {headshotPreview ? 'Replace' : 'Add photo'}
              </span>
            </button>
            <div className="agent-editor__headshot-actions">
              {headshotPreview && (
                <button type="button" className="btn btn--ghost btn--small" onClick={clearHeadshot}>
                  Remove photo
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFile}
              />
              <p className="agent-editor__headshot-hint">
                Click the circle to upload. Photo stored at original resolution.
              </p>
            </div>
          </div>

          <div className="agent-editor__fields">
            <label className="agent-editor__label">
              <span>Name *</span>
              <input
                placeholder="First Last"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </label>
            <label className="agent-editor__label">
              <span>Title or designation</span>
              <input
                placeholder="e.g. Buyer's Agent, REALTOR®"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </label>
            <label className="agent-editor__label">
              <span>Phone</span>
              <input
                type="tel"
                placeholder="(555) 123-4567"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </label>
            <label className="agent-editor__label">
              <span>Email</span>
              <input
                type="email"
                placeholder="name@example.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </label>
            <label className="agent-editor__label">
              <span>Website</span>
              <input
                type="url"
                placeholder="https://yoursite.com"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
              />
            </label>
            <label className="agent-editor__label">
              <span>Notes</span>
              <textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </label>
          </div>
        </div>

        </div>{/* /modal__body */}

        <footer className="modal__footer">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--hero"
            disabled={!name.trim() || saveMut.isPending}
            onClick={() => saveMut.mutate()}
          >
            {saveMut.isPending ? 'Saving…' : isNew ? 'Add member' : 'Save'}
          </button>
        </footer>
      </div>
    </div>
  );
}
